import { useState } from "react";
import { Link } from "react-router";
import { apiFetch } from "@contractor-quoting/api-client";
import { Badge, Card } from "@contractor-quoting/shared-ui";

export function CustomerDirectory({
  items,
}: {
  items: Array<Record<string, unknown> & { id?: string }>;
}) {
  const [expanded, setExpanded] = useState<string | null>(null);
  const [histories, setHistories] = useState<Record<string, Array<Record<string, unknown>>>>({});
  const [loading, setLoading] = useState<string | null>(null);
  const toggle = async (id: string) => {
    if (expanded === id) {
      setExpanded(null);
      return;
    }
    setExpanded(id);
    if (histories[id]) return;
    setLoading(id);
    try {
      const result = await apiFetch<{ items: Array<Record<string, unknown>> }>(
        `/v1/contractor/customers/${id}/projects`,
      );
      setHistories((current) => ({ ...current, [id]: result.items }));
    } finally {
      setLoading(null);
    }
  };
  return (
    <div className="mt-8 grid gap-4">
      {items.map((item, index) => {
        const id = item.id ?? String(index);
        const projects = histories[id] ?? [];
        return (
          <Card key={id}>
            <button
              aria-expanded={expanded === id}
              className="flex w-full items-center justify-between gap-4 text-left"
              onClick={() => item.id && void toggle(item.id)}
            >
              <div>
                <h2 className="font-bold">
                  {String(item.display_name ?? item.email ?? `Customer ${index + 1}`)}
                </h2>
                <p className="mt-1 flex flex-wrap gap-x-2 text-sm text-stone-500">
                  {item.email ? <span>{String(item.email)}</span> : null}
                  {item.phone ? <span>{String(item.phone)}</span> : null}
                </p>
              </div>
              <Badge>{String(item.project_count ?? 0)} projects</Badge>
            </button>
            {expanded === id ? (
              <div className="mt-4 border-t border-stone-200 pt-4">
                {loading === id ? (
                  <p className="text-sm text-stone-500">Loading projects…</p>
                ) : null}
                {projects.map((project) => (
                  <Link
                    className="flex items-center justify-between gap-3 rounded-xl p-3 hover:bg-stone-50"
                    key={String(project.id)}
                    to={`/contractor/projects/${String(project.id)}`}
                  >
                    <div>
                      <p className="font-semibold">
                        {String(project.title ?? project.job_type_name ?? "Project")}
                      </p>
                      <p className="text-xs text-stone-500">
                        {String(project.job_status ?? "lead").replaceAll("_", " ")}
                      </p>
                    </div>
                    {project.latest_quote_total_minor ? (
                      <span className="font-bold">
                        {new Intl.NumberFormat("en-CA", {
                          style: "currency",
                          currency: String(project.latest_quote_currency ?? "CAD"),
                        }).format(Number(project.latest_quote_total_minor) / 100)}
                      </span>
                    ) : null}
                  </Link>
                ))}
                {!loading && projects.length === 0 ? (
                  <p className="text-sm text-stone-500">No project history.</p>
                ) : null}
              </div>
            ) : null}
          </Card>
        );
      })}
      {items.length === 0 ? <Card>No customers yet.</Card> : null}
    </div>
  );
}
